/**
 * portal#61 - the forms and bundles sent to a client through the portal (Forms Hub), as
 * the client drawer's Forms tab lists them (GET /dispatch/history). The client's answers
 * live on each send's submission, not in the drawer's own form submissions, and open in the
 * Signoff Queue's preview. ehr#165 folds them into the tab's one table (lib/formsTable.ts).
 */

/** One Forms Hub send to this client. */
export interface SentFormRow {
  id: string;
  formId: string | null;
  packetId: string | null;
  sentAt: string;
  dueDate: string | null;
  form: { id: string; name: string } | null;
  packet: { id: string; name: string } | null;
  submissions: {
    id: string;
    status: string;
    submittedAt: string | null;
    /** ehr#165 - when the send opened its submission, and when the client last saved it. */
    createdAt?: string;
    updatedAt?: string;
  }[];
}

const shortDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** Submitted by the client: 'submitted', or 'completed' once signed off. */
const DONE = new Set(['submitted', 'completed', 'signed']);

/**
 * Where a send stands, in plain words. A bundle's forms are submitted one at a time in the
 * portal (portal#34) and the send itself is submitted with the last one, so an open bundle
 * may already hold answers: View shows them.
 */
export function sentStatus(row: SentFormRow): { text: string; done: boolean } {
  const sub = row.submissions[0];
  if (!sub) return { text: 'Not opened yet', done: false };
  if (!DONE.has(sub.status)) {
    return row.packetId
      ? { text: 'In progress · View shows the forms sent back so far', done: false }
      : { text: 'Waiting for the client', done: false };
  }
  const when = sub.submittedAt ? ` ${shortDate(sub.submittedAt)}` : '';
  return sub.status === 'completed' || sub.status === 'signed'
    ? { text: `Submitted${when} · signed off`, done: true }
    : { text: `Submitted${when}`, done: true };
}

/** The send's name as staff read it. */
export const sentName = (row: SentFormRow) =>
  (row.form?.name ?? row.packet?.name ?? 'Form') + (row.packetId ? ' (bundle)' : '');

/**
 * ehr#165 - a send's state for the Forms tab:
 *  - done: the client submitted it;
 *  - overdue: still open past its due date;
 *  - started: the client saved answers (the submission changed after the send opened it);
 *  - unopened: nothing back yet.
 */
export type SendState = 'done' | 'overdue' | 'started' | 'unopened';

export function sendState(row: SentFormRow, now: Date = new Date()): SendState {
  const sub = row.submissions[0];
  if (sub && DONE.has(sub.status)) return 'done';
  if (sub?.status === 'overdue' || (row.dueDate && new Date(row.dueDate) < now)) return 'overdue';
  const opened = sub?.createdAt ? new Date(sub.createdAt).getTime() : NaN;
  const saved = sub?.updatedAt ? new Date(sub.updatedAt).getTime() : NaN;
  // A minute's grace, so the send's own write never reads as the client saving.
  if (saved - opened > 60_000) return 'started';
  return 'unopened';
}

/** When the client sent it back (the submission's time, else the send's). */
export const sentBackAt = (row: SentFormRow): string =>
  row.submissions[0]?.submittedAt ?? row.submissions[0]?.updatedAt ?? row.sentAt;
